// RCPT1-S3-F1 (docs/build-briefs/RCPT1-S3-F1-remaining-tip-second-tap.md; DEC-489; PRD P0.9). The Remaining tip sheet: after a manager approved a
// tip above the limit and the limit was charged, the bartender asks the guest to tap or insert a card again for the rest. One scrim-backed
// sheet built at runtime (check:chrome-ratchet refuses a new overlay written into index.html), in the Confirm Payment sheet's layout.
//
// FLOOR surface, touch only, nothing typed. iPad sizing: every button is a full-width 44px-or-taller control from the shared sheet classes.
// The sheet decides nothing about money: the figure is the server's, shown back, and the buttons only ask the server to act.
// All text via textContent.

import { remainderSheetView, type RemainderPhase, type RemainderSheetView } from "./remainingTipModel.ts";

export interface RemainingTipOutcome {
  phase: RemainderPhase;
  failureKind?: string | null;
  failureText?: string | null;
  leaveReason?: "guest_left" | "cancelled" | null;
}

export interface RemainingTipSheetOptions {
  /** The server's remainder, in whole cents. */
  remainderCents: number;
  /** Send the second charge. Resolves with where the sheet goes next. */
  onCollect: () => Promise<RemainingTipOutcome>;
  /** Stop the wait on the reader, then record the remainder as not collected (Cancel). */
  onCancelWait: () => Promise<RemainingTipOutcome>;
  /** Record the remainder as not collected because the guest has left. */
  onGuestLeft: () => Promise<RemainingTipOutcome>;
}

export interface RemainingTipSheetController {
  open: (options: RemainingTipSheetOptions) => void;
  close: () => void;
  isOpen: () => boolean;
}

const SCRIM_ID = "remaining-tip-scrim";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

function button(className: string, testId: string, onClick: () => void): HTMLButtonElement {
  const node = el("button", className, undefined, testId);
  node.type = "button";
  node.addEventListener("click", onClick);
  return node;
}

export function createRemainingTipSheet(): RemainingTipSheetController {
  let scrim: HTMLElement | undefined;

  function close(): void {
    scrim?.remove();
    scrim = undefined;
  }

  function open(options: RemainingTipSheetOptions): void {
    close();
    let outcome: RemainingTipOutcome = { phase: "ready" };
    let stopping = false;
    // A tap in flight: every control is off until the server answers, so a second tap cannot send a second request.
    let sending = false;

    const root = el("div", "scrim scrim-top", undefined, SCRIM_ID);
    root.id = SCRIM_ID;
    const sheet = el("div", "sheet pay-confirm-sheet", undefined, "remaining-tip-sheet");
    sheet.id = "remaining-tip-sheet";
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-labelledby", "remaining-tip-title");

    const header = el("div", "sheet-header");
    const titleRow = el("div", "sheet-title-row");
    const title = el("span", "sheet-title", "Remaining tip");
    title.id = "remaining-tip-title";
    titleRow.append(title);
    header.append(titleRow);

    const body = el("div", "sheet-body pay-confirm-body", undefined, "remaining-tip-body");
    const amount = el("div", "pay-confirm-amount");
    const amountLine = el("span", "pay-confirm-amount-value", undefined, "remaining-tip-amount");
    amount.append(amountLine);
    const message = el("div", "awaiting-tip-live", undefined, "remaining-tip-message");
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    body.append(amount, message);

    const footer = el("div", "sheet-footer pay-confirm-actions");
    const leave = button("btn-secondary btn-full", "remaining-tip-left", () => void run(options.onGuestLeft));
    const cancel = button("btn-secondary btn-full", "remaining-tip-cancel", () => void cancelWait());
    const collect = button("btn-pay", "remaining-tip-collect", () => void collectNow());
    collect.id = "remaining-tip-collect";
    const done = button("btn-pay", "remaining-tip-done", () => close());
    footer.append(leave, cancel, collect, done);

    sheet.append(header, body, footer);
    root.append(sheet);
    document.body.append(root);
    scrim = root;

    function draw(view: RemainderSheetView): void {
      amountLine.textContent = view.amountLine;
      message.textContent = view.message;
      message.classList.toggle("is-danger", view.tone === "danger");
      collect.hidden = view.collectLabel === null;
      collect.textContent = view.collectLabel ?? "";
      collect.disabled = sending || !view.collectEnabled;
      cancel.hidden = view.cancelLabel === null;
      cancel.textContent = view.cancelLabel ?? "";
      cancel.disabled = !view.cancelEnabled;
      leave.hidden = view.leaveLabel === null;
      leave.textContent = view.leaveLabel ?? "";
      leave.disabled = sending;
      done.hidden = view.doneLabel === null;
      done.textContent = view.doneLabel ?? "";
    }

    function render(): void {
      draw(
        remainderSheetView({
          remainderCents: options.remainderCents,
          phase: outcome.phase,
          failureKind: outcome.failureKind ?? null,
          failureText: outcome.failureText ?? null,
          stopping,
          leaveReason: outcome.leaveReason ?? null,
        }),
      );
    }

    // Collect shows the waiting state at once, then whatever the server said.
    async function collectNow(): Promise<void> {
      if (sending || outcome.phase === "waiting") return;
      sending = true;
      outcome = { phase: "waiting" };
      render();
      let next: RemainingTipOutcome;
      try {
        next = await options.onCollect();
      } catch {
        next = {
          phase: "pending",
        };
      }
      if (scrim !== root) return;
      // Cancel was handled while this request was open: its recorded outcome stands, and this late answer is dropped.
      if (outcome.phase !== "waiting") return;
      sending = false;
      outcome = next;
      render();
    }

    async function run(action: () => Promise<RemainingTipOutcome>): Promise<void> {
      if (sending) return;
      sending = true;
      render();
      let next: RemainingTipOutcome;
      try {
        next = await action();
      } catch {
        next = { phase: outcome.phase, failureText: "Could not reach the server. Check before trying again." };
      }
      if (scrim !== root) return;
      sending = false;
      outcome = next;
      render();
    }

    // Cancel while the reader waits: the stop is sent while the collect request is still open; when it answers, the sheet is
    // already showing the recorded outcome and the late answer is ignored.
    async function cancelWait(): Promise<void> {
      if (stopping) return;
      stopping = true;
      render();
      let next: RemainingTipOutcome;
      try {
        next = await options.onCancelWait();
      } catch {
        next = { phase: "failed", failureText: "Could not stop the reader. Check the reader screen." };
      }
      stopping = false;
      if (scrim !== root) return;
      sending = false;
      outcome = next;
      render();
    }

    render();
  }

  return { open, close, isOpen: () => scrim !== undefined };
}
